import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Modal,
  IconButton,
  Tooltip,
  Grid2,
  Button,
  Card,
} from '@mui/material';
import {
  EmbryoEditValues,
  createGradingHistory,
  getPatientHistoryResultList,
  mapPatientHistoryResults,
} from '../EmbryoGradingAi.helper';
import { useTranslation } from 'react-i18next';
import EmbryoListForGrading from '../EmbryoListForGrading';
import EmbryoDetailsLayout from '../EmbryoDetailsLayout';
import { useGradingHistory } from '../GradingHistoryContext';
import ResultList from './ResultList';
import HistoryTable from './HistoryTable';
import { ReactComponent as AiSparkle } from '../../../assets/SVG/aiSparkle.svg';
import styles from '../EmbryoGradingAI.module.css';
import { ReactComponent as BackIcon } from '../../../assets/SVG/aiBackButtonSP.svg';
import AiGradient from '../AiGradient';
import { usePatientContext } from '../../../components/SubPatinetProvider';
import { useCycle } from '../../../components/cycleContext/CycleContext';
import { useAlert } from '../../../components/alertProvider';
import ResultLoader from '../ResultLoader';

/* -------------------- Styles -------------------- */
const scoreSx = {
  fontFamily: 'Montserrat',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '1',
  textAlign: 'center',
  marginBottom: 1,
  background: 'linear-gradient(103.98deg,rgba(240, 154, 89, 0.9) 0%, rgba(165, 65, 223, 0.9) 50.5%, rgba(57, 159, 197, 0.9) 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  position: 'absolute',
};

const cardSx = {
  padding: 2,
  borderRadius: 4,
  backgroundColor: '#FFFFFF',
  border: '1px solid #F0F0F0',
  position: 'relative',
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  height: '100%',
  ml: 0.5,
};

const boxContainerSx = {
  width: 1200,
  height: 800,
  bgcolor: 'background.paper',
  borderRadius: 4,
  mx: 'auto',
  mt: '10%',
  boxShadow: 24,
  outline: 'none',
  overflow: 'hidden',
};

const fullScreenBoxSx = {
  width: '100%',
  height: '100vh',
  bgcolor: 'background.paper',
  borderRadius: 0,
  boxShadow: 'none',
  outline: 'none',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

const headerSx = {
  fontFamily: 'Montserrat',
  fontWeight: 700,
  fontSize: '18px',
  color: '#232323',
};

const subHeaderSx = {
  fontFamily: 'Montserrat',
  fontWeight: 700,
  fontSize: '16px',
  color: '#232323',
};

const closeIconSx = {
  color: '#9E9E9E',
  '&:hover': {
    backgroundColor: 'rgba(158, 158, 158, 0.1)',
  },
};

const AiSparkleSx = {
  borderRadius: 3,
  textTransform: 'none',
  fontFamily: 'Montserrat',
  fontWeight: 700,
  fontSize: '16px',
  padding: 1,
  pr: 3,
  pl: 2,
  background:
    'linear-gradient(90deg,rgba(227, 202, 242, 0.2) 0%,rgba(243, 230, 185, 0.2) 33%, rgba(244, 206, 204, 0.2) 68.5%,rgba(191, 221, 242, 0.2) 100%)',
  color: '#232323',
  boxShadow: 'none',
  border: '1px solid #E8E8E8',
  mb: 1,
};

const exitSx = {
  borderRadius: 3,
  textTransform: 'none',
  fontFamily: 'Montserrat',
  fontWeight: 600,
  fontSize: '14px',
  backgroundColor: '#666666',
  '&:hover': {
    backgroundColor: '#555555',
  },
  padding: 0.7,
};

/* -------------------- Component -------------------- */
interface EditEmbryoInformationModalProps {
  open?: boolean;
  IsFromAimbryoDetails?: boolean;
  onClose: () => void;
  onSave?: (values: EmbryoEditValues) => void;
  initialValues?: EmbryoEditValues;
}

const SinglePatientGradingModal: React.FC<EditEmbryoInformationModalProps> = ({
  open = false,
  IsFromAimbryoDetails = false,
  onClose,
}) => {
  const { t: tAiGrading } = useTranslation('aiGrading_config');
  const navigate = useNavigate();
  const {
    isResultAvailable,
    showFullResult,
    setShowFullResult,
    selectedResultFromResultLList,
    resultListForSelectedPatient,
    setResultListForSelectedPatient,
    setSelectedResultFromResultList,
    setSelectedPatientForGrading,
  } = useGradingHistory();

  const { patientId } = usePatientContext();
  const { cycleIdForFertility } = useCycle();
  const { addAlert } = useAlert();

  // get updated result list
  const getUpdatedResultList = () => {
    getPatientHistoryResultList({ patient_id: patientId })
      .then((response) => {
        if (response && Array.isArray(response)) {
          mapPatientHistoryResults(
            response,
            setSelectedPatientForGrading,
            setResultListForSelectedPatient,
            setSelectedResultFromResultList,
          );
        } else {
          addAlert(tAiGrading('somethingWentWrong'), 'error');
        }
      })
      .catch(() => {
        addAlert(tAiGrading('somethingWentWrong'), 'error');
      });
  };

  const handleGenerateNewGrading = () => {
    const payload = {
      patients: [
        {
          patient_id: patientId,
          cycle_id: cycleIdForFertility,
        },
      ],
    };

    createGradingHistory(payload)
      .then(() => {
        addAlert(
          tAiGrading('newGradingGeneratedSuccess', {
            defaultValue: 'New grading generated successfully',
          }),
          'success',
        );
        getUpdatedResultList();
      })
      .catch(() => {
        addAlert(tAiGrading('somethingWentWrong'), 'error');
      });
  };

  /* -------------------- Shared Content -------------------- */
  const Content = (
    <Box sx={IsFromAimbryoDetails ? fullScreenBoxSx : boxContainerSx}>
      {/* Header */}
      {!IsFromAimbryoDetails && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={1}
          px={2}
          borderBottom={'1px solid rgba(226, 227, 229, 1)'}
        >
          <Typography sx={headerSx}>
            {tAiGrading('aIEmbryoGrader', { defaultValue: 'AI Embryo Grader' })}
          </Typography>

          <Tooltip title={tAiGrading('exit', { defaultValue: 'Exit' })} arrow>
            <Button variant="contained" sx={exitSx} onClick={onClose}>
              {tAiGrading('exit', { defaultValue: 'Exit' })}
            </Button>
          </Tooltip>
        </Box>
      )}

      {/* Subheader and main content */}
      {!showFullResult ? (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={1}
          px={2}
          borderBottom={'1px solid rgba(226, 227, 229, 1)'}
        >
          <Typography sx={subHeaderSx}>
            {tAiGrading('aiGradingDetails', {
              defaultValue: 'AI Grading Details',
            })}
          </Typography>
          <Tooltip
            title={tAiGrading('generateNewResult', {
              defaultValue: 'Generate New Result',
            })}
            arrow
          >
            <Button
              variant="contained"
              sx={AiSparkleSx}
              onClick={handleGenerateNewGrading}
            >
              <AiSparkle className={styles.AiSparkle} />{' '}
              {tAiGrading('generateNewResult', {
                defaultValue: 'Generate New Result',
              })}
            </Button>
          </Tooltip>
        </Box>
      ) : (
        <Box
          display="flex"
          alignItems="center"
          justifyContent={'space-between'}
          p={1}
          px={2}
          borderBottom={'1px solid rgba(226, 227, 229, 1)'}
        >
          <Box display={'flex'} alignItems={'center'} gap={2}>
            <Tooltip title={tAiGrading('close')} arrow>
              <IconButton onClick={() => setShowFullResult(false)} sx={closeIconSx}>
                <BackIcon />
              </IconButton>
            </Tooltip>

            <Typography sx={headerSx}>
              {tAiGrading('result', { defaultValue: 'Result' })} -{' '}
              {selectedResultFromResultLList?.index}
            </Typography>
          </Box>

          <Box>
            <Tooltip
              title={tAiGrading('OpenAIEMBRYO', {
                defaultValue: 'Open AIEMBRYO',
              })}
              arrow
            >
              <Button
                variant="contained"
                sx={AiSparkleSx}
                onClick={() => {
                  if (selectedResultFromResultLList) {
                    const sessionId = selectedResultFromResultLList.session_id;
                    const patientId = selectedResultFromResultLList.patient_id;
                    const params = new URLSearchParams();
                    if (sessionId != null) params.append('Session_id', String(sessionId));
                    if (patientId != null) params.append('Patient_id', String(patientId));
                    navigate(`/ai/aiEmbryoGrader?${params.toString()}`);
                  }
                }}
              >
                <AiSparkle className={styles.AiSparkle} />{' '}
                {tAiGrading('OpenAIEMBRYO', { defaultValue: 'Open AIEMBRYO' })}
              </Button>
            </Tooltip>
          </Box>
        </Box>
      )}

      <Box width={'100%'} height={'82%'} p={2}>
        {showFullResult ? (
          isResultAvailable ? (
            <Grid2 container height={'100%'} display="flex" justifyContent="center" size={12}>
              <Grid2 size={2.5} height={'100%'}>
                <EmbryoListForGrading />
              </Grid2>
              <Grid2 size={9.5} height={'100%'}>
                <EmbryoDetailsLayout />
              </Grid2>
            </Grid2>
          ) : (
            <Card elevation={0} sx={cardSx}>
              <ResultLoader
                title={tAiGrading('waitwhileGeneratingAIEmbryoGrading', {
                  defaultValue: 'Please wait while generating AI embryo grading...',
                })}
              />
            </Card>
          )
        ) : resultListForSelectedPatient.length ? (
          <Grid2 container height={'100%'} display="flex" justifyContent="center" size={12}>
            <Grid2 size={2.5} height={'100%'}>
              <ResultList />
            </Grid2>
            <Grid2 size={9.5} height={'100%'}>
              <HistoryTable />
            </Grid2>
          </Grid2>
        ) : (
          <Card elevation={0} sx={cardSx}>
            <AiGradient height={'400px'} width={'400px'} />
            <Typography sx={scoreSx}>
              {tAiGrading('noResultGeneratedYet', {
                defaultValue: 'No result Generated yet',
              })}
            </Typography>
          </Card>
        )}
      </Box>
    </Box>
  );

  /* -------------------- Render -------------------- */
  return IsFromAimbryoDetails ? (
    Content
  ) : (
    <Modal open={open} onClose={onClose}>
      {Content}
    </Modal>
  );
};

export default SinglePatientGradingModal;
